import React, { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { Layout, Dropdown, Drawer, Button } from "antd";
import { UserOutlined, LogoutOutlined, MenuOutlined } from "@ant-design/icons";
import { useAuth } from "../context/AuthContext";
import { NavGuardProvider, useNavGuardRunner } from "../context/NavGuardContext.js";
import useIsMobile from "../hooks/useIsMobile.js";
import TopNav from "./TopNav.js";

// "Levent Tasci" → "LT"; falls back to the e-mail's first letter
function initialsOf(user) {
  const words = (user?.name || "").trim().split(/\s+/).filter(Boolean);
  if (words.length) return words.slice(0, 2).map((w) => w[0].toUpperCase()).join("");
  return (user?.email || "?")[0].toUpperCase();
}

const { Header, Content, Footer } = Layout;

// Hardcoded to the one tenant that exists today — when a second tenant is
// added, this becomes derived from the logged-in user's own tenant instead.
const TENANT_BASE = "/proyapi";

const BASE_NAV_ITEMS = [
  { key: `${TENANT_BASE}`,              label: "Welcome"       },
  { key: `${TENANT_BASE}/digitwin`,      label: "DigiTwin"      },
  { key: `${TENANT_BASE}/bimframework`,  label: "BIM Framework" },
  { key: `${TENANT_BASE}/softwares`,     label: "Softwares"     },
  { key: `${TENANT_BASE}/learn`,         label: "Learn"         },
  { key: `${TENANT_BASE}/team`,          label: "Team"          },
  { key: `${TENANT_BASE}/roadmap`,       label: "Roadmap"       },
];

// NavGuardProvider has to sit above both this component (which triggers navigation) and the
// routed pages rendered via <Outlet/> below (DigiTwin's BimViewer registers a guard on it, to
// warn about losing loaded models) — wrapped in the default export below.
function AppLayout() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user, logout } = useAuth();
  const isMobile  = useIsMobile();
  const [navDrawerOpen, setNavDrawerOpen] = useState(false);
  const runNavGuard = useNavGuardRunner();
  const path      = location.pathname;
  const segments  = path.split("/").filter(Boolean); // ["proyapi", "digitwin"] or ["proyapi"]
  const activeKey = segments.length <= 1 ? TENANT_BASE : `${TENANT_BASE}/${segments[1]}`;
  const navItems  = user?.isAdmin
    ? [...BASE_NAV_ITEMS, { key: `${TENANT_BASE}/admin`, label: "Admin" }]
    : BASE_NAV_ITEMS;

  const goTo = (key) => runNavGuard(() => navigate(key));

  async function handleLogout() {
    await logout();
    navigate("/");
  }

  return (
    <Layout style={{ height: "100vh", overflow: "hidden" }}>
      <Header className="app-header" style={{ display: "flex", alignItems: "center", padding: isMobile ? "0 12px" : "0 24px", gap: 10, flexShrink: 0 }}>
        <div
          style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", flexShrink: 0 }}
          onClick={() => goTo(TENANT_BASE)}
        >
          <img src="/proyapi-header.png" alt="PROYAPI" style={{ height: 20 }} />
          {!isMobile && (
            <span style={{
              color: "rgba(255,255,255,0.85)",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 600,
              whiteSpace: "nowrap",
              fontSize: 14,
            }}>
              Digital <em style={{ fontStyle: "normal", color: "#4f9cf9" }}>Infrastructure</em>
            </span>
          )}
        </div>

        {isMobile ? (
          <>
            {/* Page nav collapses into a Drawer on mobile instead of relying on antd Menu's
                built-in horizontal overflow ("...") — when the active page happened to be one
                of the items pushed into that overflow, it inherited the selected-item's solid
                blue highlight, showing up as an unstyled blue block sitting oddly right next
                to the account icon. */}
            <div style={{ flex: 1 }} />
            <Button
              type="text"
              icon={<MenuOutlined style={{ color: "rgba(255,255,255,0.75)" }} />}
              onClick={() => setNavDrawerOpen(true)}
              style={{ flexShrink: 0 }}
            />
            <Drawer
              placement="right"
              width="72%"
              open={navDrawerOpen}
              onClose={() => setNavDrawerOpen(false)}
              closable={false}
              rootClassName="tn-drawer"
              styles={{ body: { padding: "18px 14px" } }}
            >
              {/* Same pill look as the desktop TopNav; static here, since the drawer closes on tap */}
              <div className="tn-mobile-label">Pages</div>
              <nav className="tn-mobile">
                {navItems.map((it) => (
                  <button
                    key={it.key}
                    type="button"
                    className={`tn-mobile-item${it.key === activeKey ? " is-active" : ""}`}
                    aria-current={it.key === activeKey ? "page" : undefined}
                    onClick={() => { setNavDrawerOpen(false); goTo(it.key); }}
                  >
                    {it.label}
                  </button>
                ))}
              </nav>
            </Drawer>
          </>
        ) : (
          <TopNav items={navItems} activeKey={activeKey} onSelect={goTo} />
        )}

        <Dropdown
          menu={{
            items: [
              { key: "logout", label: "Log out", icon: <LogoutOutlined /> },
            ],
            onClick: ({ key }) => key === "logout" && handleLogout(),
          }}
          trigger={["click"]}
        >
          <div className={`tn-account${isMobile ? " is-compact" : ""}`} title={user?.email || ""}>
            <span className="tn-avatar">{user ? initialsOf(user) : <UserOutlined />}</span>
            {!isMobile && (user?.name || user?.email || "Account")}
          </div>
        </Dropdown>
      </Header>

      <Content style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column", minHeight: 0 }}>
        <Outlet />
      </Content>

      <Footer style={{
        height: 34,
        padding: "0 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        borderTop: "1px solid rgba(255,255,255,0.08)",
        background: "transparent",
      }}>
        <span style={{
          fontFamily: "'DM Mono', monospace",
          fontSize: 11,
          color: "rgba(255,255,255,0.25)",
          letterSpacing: "0.06em",
        }}>
          Created by Levent Tasci — 2026
        </span>
      </Footer>
    </Layout>
  );
}

export default function AppLayoutWithNavGuard() {
  return (
    <NavGuardProvider>
      <AppLayout />
    </NavGuardProvider>
  );
}
